import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useGetOrderDetail from "../../../apis/useGetOrderDetail/useGetOrderDetail";
import { formatToDisplayTime } from "../../../common/helpers/stringUtils";
import { GST_REGEX, findTaxEntry } from "../../../common/helpers/taxHelper";
import { useLocalStorage } from "../../../common/hooks/useLocalStorage";
import { useCartActions } from "../../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../../contexts/ConfigContext/ConfigContext";
import { IStallMode } from "../../../contexts/ConfigContext/types";
import { usePaymentActions } from "../../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../../router/path";
import { addDatadogActionWithContext } from "../../../services/datadog";
import OrderFail from "../OrderFail/OrderFail";
import OrderLayoutDefault from "../OrderLayoutDefault/OrderLayoutDefault";
import OrderLayoutWang from "../OrderLayoutWang/OrderLayoutWang";
import OrderLoading from "../OrderLoading/OrderLoading";

const OrderContainer: React.FC = () => {
	const { orderDetail, isLoading, error } = useGetOrderDetail();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { state: stallMode } = useConfigContext((state) => state.stallMode);
	const { CLEAN_CART } = useCartActions();
	const paymentActions = usePaymentActions();
	const [showPaymentReceipt, setShowPaymentReceipt] = useState(false);

	const { setValue: setProductListScroll } = useLocalStorage("PRODUCT_LIST_SCROLL", {
		scrollTop: "0",
		activeCategoryId: null,
	});

	const handleNewOrder = useCallback(() => {
		CLEAN_CART();
		// The paid order's reference must not follow the customer into the next one -
		// left behind, the cart's checkout button reads it on mount and flashes the
		// payment page before the clear lands.
		paymentActions.CLEAR_PAYMENT_DATA();
		setProductListScroll({
			scrollTop: "0",
			activeCategoryId: null,
		});
		navigate(PATH.PRODUCT_LIST);
	}, [CLEAN_CART, paymentActions, navigate, setProductListScroll]);

	const handleTogglePaymentReceipt = useCallback(() => {
		setShowPaymentReceipt((prev) => !prev);
	}, []);

	// Track order confirmation page load with payment data
	useEffect(() => {
		if (orderDetail && !isLoading) {
			addDatadogActionWithContext(
				"order_confirmation_loaded",
				{
					order_id: orderDetail.id,
					total_amount: orderDetail.total_amount,
					timestamp: new Date().toISOString(),
				},
				orderDetail.payment_reference || undefined,
				true
			);
		}
	}, [orderDetail, isLoading]);

	// Handle loading and error states
	if (isLoading) {
		return <OrderLoading />;
	}

	if (error || !orderDetail) {
		return (
			<OrderFail errorMessage={t("orderConfirmation.errorLoadingOrder")} isPaymentFailure={false} />
		);
	}

	// Calculate derived values
	const totalAmount = orderDetail.order_items.orders_figures.net_value;
	const orderType = orderDetail.order_items.is_takeaway
		? t("orderConfirmation.orderType.takeaway")
		: t("orderConfirmation.orderType.dinein");
	const gstRate = findTaxEntry(orderDetail.order_items.tax, GST_REGEX)?.rate ?? 0;
	const orderTimestamp = formatToDisplayTime(orderDetail.created_at);
	// Choose layout based on stallMode
	const renderOrderLayout = () => {
		const commonProps = {
			orderDetail,
			showPaymentReceipt,
			onTogglePaymentReceipt: handleTogglePaymentReceipt,
			onNewOrder: handleNewOrder,
			totalAmount,
			orderType,
			gstRate,
			orderTimestamp,
		};

		if (stallMode === IStallMode.WANG) {
			return <OrderLayoutWang {...commonProps} />;
		}

		return <OrderLayoutDefault {...commonProps} />;
	};

	return renderOrderLayout();
};

export default OrderContainer;
